<script setup lang="ts">
// ================================
// IMPORTS
// ================================
import { computed, inject, nextTick, onBeforeMount, ref, watch } from "vue"
import { useRoute } from "vue-router"
import { useI18n } from "vue-i18n"
import { useAuthStore } from "@core/stores/auth.store"
import type { CompanyType, OwnerOutput } from "@core/composables"
import type {
    ConsentRequestMultiFormat,
    ConsentRequestMultiItem,
    MultiConsentAddressRecap,
    MultiConsentBuildingRecap,
} from "@core/types/source/multi_consent"
import type { Address } from "@types"
import type { Services } from "@services"
import { CONSENT_REQUEST_MULTI_STATE, SOURCES_TYPES } from "@core/constants"

// ================================
// INTERNATIONALIZATION
// ================================
const { t } = useI18n({
    messages: {
        fr: {
            /** Titre principal du parcours (aligné PageConsentShareInvitation). */
            page_title: "Signature des consentements",
            title_expired: "Demande indisponible",
            title_refused: "Signature refusée",
            title_accepted: "Signature acceptée",
            loading: "Récupération des informations, veuillez patienter...",
            expired:
                "Le lien a expiré car la demande est terminée ou bien elle n'existe plus.",
            refused:
                "La demande a bien été refusée. Vous pouvez quitter cette page.",
            accepted:
                "La demande a bien été acceptée. Vous pouvez quitter cette page.",
            message: {
                pdl: "M/Mme {userTo}, vous êtes invité à signer les consentements nécessaires pour autoriser à votre conseiller M/Mme {userFrom} l'accès aux données d'un ou plusieurs compteurs d'électricité.",
                pce: "M/Mme {userTo}, vous êtes invité à signer les consentements nécessaires pour autoriser à votre conseiller M/Mme {userFrom} l'accès aux données d'un ou plusieurs compteurs de gaz.",
                both: "M/Mme {userTo}, vous êtes invité à signer les consentements nécessaires pour autoriser à votre conseiller M/Mme {userFrom} l'accès aux données d'un ou plusieurs compteurs d'électricité et de gaz.",
            },
        },
    },
})

// ================================
// TYPES & INTERFACES
// ================================
type PageState =
    | "idle"
    | "loading"
    | "valid"
    | "expired"
    | "refused"
    | "accepted"
    | "done"

/**
 * Adresse API (récap multi) → Address attendue par OwnerOutput / formatAddress.
 */
function mapMultiConsentAddressToAddress(
    recap?: MultiConsentAddressRecap | null
): Address | null {
    if (recap == null) return null
    const street = recap.street ?? ""
    const city = recap.city ?? ""
    const zipcode = recap.zipcode ?? ""
    const country = recap.country ?? ""
    if (!street && !city && !zipcode && !country) return null
    const addr: Address = { street, city, zipcode }
    if (country !== "") addr.country = country
    return addr
}

/**
 * Clé stable pour regrouper les consentrequests par bâtiment sans fusionner tout sous "" si building._id manque.
 */
function getConsentBuildingGroupKey(
    cr: ConsentRequestMultiItem,
    index: number
): string {
    const buildingId = cr.building?._id
    if (buildingId) return buildingId

    const b = cr.building
    if (b) {
        const a = b.address
        const parts = [
            b.name ?? "",
            a?.street ?? "",
            a?.zipcode ?? "",
            a?.city ?? "",
            a?.country ?? "",
        ]
        if (parts.some((p) => p !== "")) {
            return `fp:${parts.join("|")}`
        }
    }

    if (cr._id) return `cr:${cr._id}`

    return `idx:${index}`
}

// ================================
// PROPS & EMITS
// ================================
const props = withDefaults(defineProps<{ requestIdOverride?: string }>(), {
    requestIdOverride: undefined,
})

// ================================
// COMPOSABLES
// ================================
const route = useRoute()
const authStore = useAuthStore()
const services = inject<Services>("services")!

// ================================
// STATE
// ================================
/** État courant de la page (idle → loading → valid | expired | refused | accepted | done) */
const pageState = ref<PageState>("idle")
/** Demande multi chargée (rempli en cas de succès) */
const request = ref<ConsentRequestMultiFormat | null>(null)

// ================================
// COMPUTED
// ================================
/** Titulaire (user_to) au format OwnerOutput pour SourceMultiAppendFormOnInvitation */
const getOwner = computed((): OwnerOutput | null => {
    const r = request.value?.user_to
    if (!r) return null
    const c = r.company
    return {
        user: {
            firstname: r.firstname ?? "",
            lastname: r.lastname ?? "",
            username: r.username ?? "",
        },
        company: c
            ? {
                  _id: c._id,
                  name: c.name ?? "",
                  address: mapMultiConsentAddressToAddress(c.address),
                  type: (c.type ?? "") as CompanyType | "",
                  idcompany: c.idcompany,
                  legalForm: c.legalForm,
              }
            : null,
    }
})

/**
 * Type de consentement à afficher : pdl, pce ou both selon les idtype des consentrequests.
 */
const getConsentConditionsType = computed<"pdl" | "pce" | "both">(() => {
    const items = request.value?.consentrequests ?? []
    if (items.length === 0) return "pdl"
    const hasPce = items.some((c) => c.idtype === SOURCES_TYPES.PCE)
    const hasPdl = items.some((c) => c.idtype === SOURCES_TYPES.PDL)
    if (hasPce && hasPdl) return "both"
    if (items.every((c) => c.idtype === SOURCES_TYPES.PCE)) return "pce"
    return "pdl"
})

/**
 * Liste des bâtiments avec leurs compteurs (regroupement par building).
 */
const getBuildings = computed((): MultiConsentBuildingRecap[] => {
    const items = request.value?.consentrequests ?? []
    const acc: Record<string, MultiConsentBuildingRecap> = {}
    for (let index = 0; index < items.length; index++) {
        const cr = items[index]
        const bid = getConsentBuildingGroupKey(cr, index)
        const source = {
            idsource: cr.idsource ?? "",
            idtype: (cr.idtype === SOURCES_TYPES.PCE ? "pce" : "pdl") as
                | "pdl"
                | "pce",
        }
        if (acc[bid]) {
            acc[bid].sources.push(source)
        } else {
            acc[bid] = {
                name: cr.building?.name,
                address: cr.building?.address,
                sources: [source],
            }
        }
    }
    return Object.values(acc)
})

/** Durée du consentement en mois (user_from company ou fallback 60). */
const getConsentDuration = computed((): number => {
    const duration =
        request.value?.user_from?.company?.operation?.consent_duration
    if (duration != null) return duration
    return 60
})

/** Identifiant de la demande pour le formulaire. */
const getRequestId = computed((): string => {
    return request.value?._id ?? ""
})

/** Paragraphe d'intro : libellé compteurs selon PDL / PCE / mixte. */
const getIntroParagraph = computed(() => {
    const kind = getConsentConditionsType.value
    const o = getOwner.value
    const userTo =
        `${o?.user?.firstname ?? ""} ${o?.user?.lastname ?? ""}`.trim()
    const r = request.value
    const userFrom =
        `${r?.user_from?.firstname ?? ""} ${r?.user_from?.lastname ?? ""}`.trim()
    return t(`message.${kind}`, { userTo, userFrom })
})

// ================================
// WATCHERS
// ================================
watch(
    () => (props.requestIdOverride ?? route.params._id) as string,
    async (id) => {
        if (!id) {
            await nextTick()
            if (!props.requestIdOverride && !route.params._id)
                pageState.value = "expired"
            return
        }
        pageState.value = "loading"
        try {
            const token = route.query.token as string | undefined
            const data = await services.$getConsentRequestMulti(id, token)
            request.value = data
            const st = data?.state
            if (st === CONSENT_REQUEST_MULTI_STATE.CREATED) {
                pageState.value = "valid"
            } else if (st === CONSENT_REQUEST_MULTI_STATE.REFUSED) {
                pageState.value = "refused"
            } else if (st === CONSENT_REQUEST_MULTI_STATE.ACCEPTED) {
                pageState.value = "accepted"
            } else {
                pageState.value = "expired"
            }
        } catch {
            pageState.value = "expired"
        }
    },
    { immediate: true }
)

// ================================
// INTERACTIONS
// ================================
/** Passe à l'état done après signature. */
function onDoneSign(): void {
    pageState.value = "done"
}

// ================================
// LIFECYCLE
// ================================
onBeforeMount(() => {
    authStore.logout()
})
</script>

<template>
    <div class="page-consent-sign-multi flex min-h-screen w-full flex-col">
        <ConsentRequestScopeHeader />
        <div class="flex flex-1 justify-center items-start px-4 py-8">
            <div
                class="w-full max-w-6xl flex flex-col items-center gap-6 text-center"
            >
                <!-- ================================ -->
                <!-- CHARGEMENT -->
                <!-- ================================ -->
                <div
                    v-if="pageState === 'loading'"
                    class="flex flex-col items-center gap-4 py-12"
                >
                    <BaseProgressSpinner style="width: 2rem; height: 2rem" />
                    <p class="text-sm text-gray-600">
                        {{ t("loading") }}
                    </p>
                </div>

                <!-- ================================ -->
                <!-- DEMANDE INDISPONIBLE -->
                <!-- ================================ -->
                <div
                    v-else-if="pageState === 'expired'"
                    class="flex flex-col gap-3 max-w-md"
                >
                    <h1
                        class="text-xl font-semibold text-gray-800 flex items-center justify-center gap-2"
                    >
                        {{ t("title_expired") }}
                        <i
                            class="pi pi-info-circle text-gray-500"
                            aria-hidden="true"
                        />
                    </h1>
                    <p class="text-sm text-amber-900">
                        {{ t("expired") }}
                    </p>
                </div>

                <!-- ================================ -->
                <!-- DÉJÀ REFUSÉE -->
                <!-- ================================ -->
                <div
                    v-else-if="pageState === 'refused'"
                    class="flex flex-col gap-3"
                >
                    <h1
                        class="text-xl font-semibold text-gray-800 flex items-center justify-center gap-2"
                    >
                        {{ t("title_refused") }}
                        <i
                            class="pi pi-info-circle text-gray-500"
                            aria-hidden="true"
                        />
                    </h1>
                    <p class="text-sm text-gray-700">
                        {{ t("refused") }}
                    </p>
                </div>

                <!-- ================================ -->
                <!-- DÉJÀ ACCEPTÉE (GET) -->
                <!-- ================================ -->
                <div
                    v-else-if="pageState === 'accepted'"
                    class="flex flex-col gap-3"
                >
                    <h1
                        class="text-xl font-semibold text-gray-800 flex items-center justify-center gap-2"
                    >
                        {{ t("title_accepted") }}
                        <i
                            class="pi pi-info-circle text-gray-700"
                            aria-hidden="true"
                        />
                    </h1>
                    <p class="text-sm text-gray-700">
                        {{ t("accepted") }}
                    </p>
                </div>

                <!-- ================================ -->
                <!-- FORMULAIRE -->
                <!-- ================================ -->
                <div
                    v-else-if="pageState === 'valid'"
                    class="flex flex-col gap-6 items-center w-full max-w-6xl"
                >
                    <header
                        class="flex flex-col items-center gap-6 w-full pt-2 text-center"
                    >
                        <h1
                            class="text-xl font-semibold text-gray-800 text-center"
                        >
                            {{ t("page_title") }}
                        </h1>
                    </header>
                    <p class="text-sm text-gray-800">
                        {{ getIntroParagraph }}
                    </p>
                    <div class="w-full max-w-4xl">
                        <SourceMultiAppendFormOnInvitation
                            v-if="getOwner && getRequestId"
                            :request-id="getRequestId"
                            :owner="getOwner"
                            :buildings="getBuildings"
                            :duration="getConsentDuration"
                            :type="getConsentConditionsType"
                            @done:sign="onDoneSign"
                        />
                    </div>
                </div>

                <!-- ================================ -->
                <!-- APRÈS SIGNATURE -->
                <!-- ================================ -->
                <div
                    v-else-if="pageState === 'done'"
                    class="flex flex-col gap-3"
                >
                    <h1
                        class="text-xl font-semibold text-gray-800 flex items-center justify-center gap-2"
                    >
                        {{ t("title_accepted") }}
                        <i
                            class="pi pi-info-circle text-gray-700"
                            aria-hidden="true"
                        />
                    </h1>
                    <p class="text-sm text-gray-700">
                        {{ t("accepted") }}
                    </p>
                </div>
            </div>
        </div>
    </div>
</template>
